import { useRender } from "@base-ui-components/react/use-render";
import { mergeProps } from "@base-ui-components/react/merge-props";
import { buttonClasses, ButtonVariantsProps } from "./Button.style";
import { ElementType, ReactNode } from "react";
import { useButtonGroupContext } from "@/ButtonGroup/ButtonGroupContext";
import { Loader2 } from "lucide-react";

export type ButtonProps = useRender.ComponentProps<"button"> &
  ButtonVariantsProps & {
    label?: string;
    endIcon?: ReactNode;
    startIcon?: ReactNode;
    icon?: ReactNode;
    href?: string;
    component?: ElementType;
    loading?: boolean;
  };

const Button = (props: ButtonProps) => {
  const {
    render,
    color,
    label,
    fullWidth,
    variant,
    roundedFull,
    size,
    className,
    startIcon,
    endIcon,
    icon,
    children,
    href,
    loading,
    ...rest
  } = props;

  const buttonGroupContext = useButtonGroupContext();

  const element = href ? <a /> : (render ?? <button />);

  const defaultProps: useRender.ElementProps<"button"> = {
    ...(href
      ? { href }
      : { type: "button", disabled: loading || rest.disabled }),
    children: (
      <>
        {Boolean(startIcon || loading) && (
          <span>
            {loading ? <Loader2 className="animate-spin" /> : startIcon}
          </span>
        )}
        {icon ? icon : children ? children : label}
        {Boolean(endIcon) && <span>{endIcon}</span>}
      </>
    ),

    className: buttonClasses({
      color: color || buttonGroupContext?.color,
      variant: variant || buttonGroupContext?.variant,
      fullWidth: fullWidth || buttonGroupContext?.fullWidth,
      roundedFull,
      size: size || buttonGroupContext?.size,
      class: className,
      hasIcon: Boolean(startIcon) || Boolean(endIcon),
      onlyIcon: Boolean(icon) && !children,
    }),
  };

  const renderElement = useRender({
    render: element,
    props: mergeProps<"button">(defaultProps, rest),
  });

  return renderElement;
};

export default Button;
